// Argus — SettingsWindow: tabs Tags + Notes + Auto-recording.
// Auto-recording = admin only (PHP enforce). Notes per-user. Tags admin-only modifications.

import { useEffect, useState } from 'react';
import {
  Box, Tabs, Tab, FormControl, InputLabel, Select, MenuItem, TextField, Button,
  FormGroup, FormControlLabel, Checkbox, Stack, Chip, Alert, CircularProgress, Typography, Autocomplete,
  Slider,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NoteIcon from '@mui/icons-material/Note';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import TimerIcon from '@mui/icons-material/Timer';
import { useAuthStore } from '../lib/auth';
import {
  fetchAgentSettings, patchAgentSettings, fetchAgentNotes, putAgentNotes,
  fetchAgentTags, fetchAllTags, createTag, linkTagToAgent, unlinkTagFromAgent,
  type AgentSettings, type AgentTag,
} from '../lib/agentSettings';

type ActiveTab = 'tags' | 'notes' | 'autorecord' | 'intervals';

export interface SettingsWindowProps {
  agentId: string;
  agentOnline: boolean;
}

export function SettingsWindow({ agentId }: SettingsWindowProps): JSX.Element {
  const [tab, setTab] = useState<ActiveTab>('tags');
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';

  return (
    <Box data-role="settings-panel" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Tabs data-role="settings-tabs" value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}>
        <Tab icon={<LocalOfferIcon fontSize="small" />} iconPosition="start" label="Tags" value="tags" sx={{ minHeight: 36 }} />
        <Tab icon={<NoteIcon fontSize="small" />} iconPosition="start" label="Notes" value="notes" sx={{ minHeight: 36 }} />
        <Tab icon={<RadioButtonCheckedIcon fontSize="small" />} iconPosition="start" label="Auto-recording" value="autorecord" sx={{ minHeight: 36 }} disabled={!isAdmin} />
        <Tab icon={<TimerIcon fontSize="small" />} iconPosition="start" label="Intervals" value="intervals" sx={{ minHeight: 36 }} disabled={!isAdmin} />
      </Tabs>
      <Box data-role="settings-content" sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {tab === 'tags' && <TagsPanel agentId={agentId} isAdmin={isAdmin} />}
        {tab === 'notes' && <NotesPanel agentId={agentId} />}
        {tab === 'autorecord' && (isAdmin ? <AutoRecordPanel agentId={agentId} /> : <Alert severity="warning">Admin role required.</Alert>)}
        {tab === 'intervals' && (isAdmin ? <IntervalsPanel agentId={agentId} /> : <Alert severity="warning">Admin role required.</Alert>)}
      </Box>
    </Box>
  );
}

function TagsPanel({ agentId, isAdmin }: { agentId: string; isAdmin: boolean }): JSX.Element {
  const [agentTags, setAgentTags] = useState<AgentTag[]>([]);
  const [allTags, setAllTags] = useState<AgentTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const [agent, all] = await Promise.all([fetchAgentTags(agentId), fetchAllTags()]);
      setAgentTags(agent);
      setAllTags(all);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void reload(); }, [agentId]);

  const handleLink = async (_: React.SyntheticEvent, value: AgentTag | null) => {
    if (!value || !isAdmin) return;
    setSubmitting(true);
    try {
      await linkTagToAgent(agentId, value.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link tag');
    } finally {
      setSubmitting(false);
    }
  };
  const handleUnlink = async (tagId: string) => {
    if (!isAdmin) return;
    setSubmitting(true);
    try {
      await unlinkTagFromAgent(agentId, tagId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink tag');
    } finally {
      setSubmitting(false);
    }
  };
  const handleCreate = async () => {
    if (!isAdmin || newTagName.trim() === '') return;
    setSubmitting(true);
    try {
      await createTag(newTagName.trim(), newTagColor);
      setNewTagName('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tag');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>;
  const availableToLink = allTags.filter((t) => !agentTags.find((a) => a.id === t.id));

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
      <Box>
        <Typography variant="overline" color="text.secondary">Tags on this agent</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, minHeight: 32 }}>
          {agentTags.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No tags linked.</Typography>
          ) : agentTags.map((t) => (
            <Chip
              key={t.id}
              label={t.name}
              size="small"
              sx={{ backgroundColor: t.color ?? undefined, color: t.color ? '#fff' : undefined }}
              onDelete={isAdmin ? () => void handleUnlink(t.id) : undefined}
              disabled={submitting}
            />
          ))}
        </Box>
      </Box>
      {isAdmin ? (
        <>
          <Box>
            <Typography variant="overline" color="text.secondary">Add existing tag</Typography>
            <Autocomplete
              size="small"
              options={availableToLink}
              getOptionLabel={(t) => t.name}
              onChange={handleLink}
              disabled={submitting || availableToLink.length === 0}
              renderInput={(p) => <TextField {...p} placeholder="Search tag…" />}
              sx={{ mt: 0.5 }}
            />
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Create new tag</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <TextField size="small" label="Name" value={newTagName} onChange={(e) => setNewTagName(e.target.value)} sx={{ flexGrow: 1 }} />
              <TextField size="small" label="Color" type="color" value={newTagColor} onChange={(e) => setNewTagColor(e.target.value)} sx={{ width: 80 }} />
              <Button size="small" variant="contained" onClick={() => void handleCreate()} disabled={submitting || newTagName.trim() === ''}>Create</Button>
            </Stack>
          </Box>
        </>
      ) : (
        <Alert severity="info">Only admins can create or link tags.</Alert>
      )}
    </Stack>
  );
}

function NotesPanel({ agentId }: { agentId: string }): JSX.Element {
  const [content, setContent] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchAgentNotes(agentId)
      .then((n) => { setContent(n.contentMd); setLoaded(true); })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [agentId]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await putAgentNotes(agentId, content);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>;
  return (
    <Stack spacing={1}>
      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
      {saved ? <Alert severity="success">Saved.</Alert> : null}
      <Typography variant="caption" color="text.secondary">
        Notes are private (per-operator). Markdown supported (rendering in a later iteration).
      </Typography>
      <TextField multiline minRows={10} maxRows={20} value={content} onChange={(e) => setContent(e.target.value)} placeholder="# Notes about this agent…" disabled={!loaded} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </Box>
    </Stack>
  );
}

const INTERVAL_MARKS = [
  { value: 10, label: '10s' },
  { value: 20, label: '20s' },
  { value: 30, label: '30s' },
  { value: 60, label: '60s' },
];

function IntervalsPanel({ agentId }: { agentId: string }): JSX.Element {
  const [settings, setSettings] = useState<AgentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchAgentSettings(agentId)
      .then(setSettings)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [agentId]);

  if (loading || !settings) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>;

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const next = await patchAgentSettings(agentId, {
        heartbeatIntervalSeconds: settings.heartbeatIntervalSeconds,
        thumbnailIntervalSeconds: settings.thumbnailIntervalSeconds,
      });
      setSettings(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
      {saved ? <Alert severity="success">Saved.</Alert> : null}
      <Typography variant="caption" color="text.secondary">
        Setări per-agent. Modificările se aplică live (push WS la agent online); offline → la următoarea conectare.
      </Typography>

      <Box>
        <Typography variant="overline" color="text.secondary">Heartbeat interval</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Frecvență mesaj heartbeat agent → server (status alive + metrics CPU/RAM/Disk pentru chart System Info).
          Valori mici = mai actual chart, dar mai multă încărcare DB. Default: 10s.
        </Typography>
        <Slider
          value={settings.heartbeatIntervalSeconds}
          onChange={(_, v) => setSettings({ ...settings, heartbeatIntervalSeconds: typeof v === 'number' ? v : v[0]! })}
          step={null}
          marks={INTERVAL_MARKS}
          min={10}
          max={60}
          valueLabelDisplay="on"
          valueLabelFormat={(v) => `${v}s`}
          sx={{ maxWidth: 480, ml: 2 }}
        />
      </Box>

      <Box>
        <Typography variant="overline" color="text.secondary">Thumbnail capture interval</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Frecvență captură + upload thumbnail desktop (320×180 PNG) pentru previzualizare în sidebar.
          Valori mici = mai puține "frame skip" în sidebar, dar mai mult CPU agent + bandwidth. Default: 60s.
        </Typography>
        <Slider
          value={settings.thumbnailIntervalSeconds}
          onChange={(_, v) => setSettings({ ...settings, thumbnailIntervalSeconds: typeof v === 'number' ? v : v[0]! })}
          step={null}
          marks={INTERVAL_MARKS}
          min={10}
          max={60}
          valueLabelDisplay="on"
          valueLabelFormat={(v) => `${v}s`}
          sx={{ maxWidth: 480, ml: 2 }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </Box>
    </Stack>
  );
}

function AutoRecordPanel({ agentId }: { agentId: string }): JSX.Element {
  const [settings, setSettings] = useState<AgentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchAgentSettings(agentId)
      .then(setSettings)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [agentId]);

  if (loading || !settings) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>;

  const sources = settings.autoRecordSources ?? [];
  const toggleSource = (src: string) => {
    const next = sources.includes(src) ? sources.filter((s) => s !== src) : [...sources, src];
    setSettings({ ...settings, autoRecordSources: next });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const next = await patchAgentSettings(agentId, {
        autoRecordOnConnect: settings.autoRecordOnConnect,
        autoRecordSources: settings.autoRecordSources,
        autoRecordRotateSeconds: settings.autoRecordRotateSeconds,
        customRetentionDays: settings.customRetentionDays,
      });
      setSettings(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
      {saved ? <Alert severity="success">Saved.</Alert> : null}
      <FormGroup>
        <FormControlLabel
          control={<Checkbox checked={settings.autoRecordOnConnect} onChange={(e) => setSettings({ ...settings, autoRecordOnConnect: e.target.checked })} />}
          label="Start recording automatically when agent connects"
        />
      </FormGroup>
      <Box>
        <Typography variant="overline" color="text.secondary">Sources</Typography>
        <FormGroup row>
          {(['screen', 'camera', 'audio'] as const).map((src) => (
            <FormControlLabel key={src} control={<Checkbox checked={sources.includes(src)} onChange={() => toggleSource(src)} disabled={!settings.autoRecordOnConnect} />} label={src} />
          ))}
        </FormGroup>
      </Box>
      <FormControl size="small" sx={{ maxWidth: 280 }}>
        <InputLabel>Rotate segments every</InputLabel>
        <Select label="Rotate segments every" value={settings.autoRecordRotateSeconds ?? 30} onChange={(e) => setSettings({ ...settings, autoRecordRotateSeconds: Number(e.target.value) })} disabled={!settings.autoRecordOnConnect}>
          {[10, 30, 60, 120, 300].map((v) => <MenuItem key={v} value={v}>{v} seconds</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ maxWidth: 280 }}>
        <InputLabel>Custom retention (days)</InputLabel>
        <Select label="Custom retention (days)" value={settings.customRetentionDays ?? 0} onChange={(e) => { const v = Number(e.target.value); setSettings({ ...settings, customRetentionDays: v === 0 ? null : v }); }}>
          <MenuItem value={0}>Default (30)</MenuItem>
          {[7, 14, 30, 60, 90, 180].map((v) => <MenuItem key={v} value={v}>{v} days</MenuItem>)}
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </Box>
    </Stack>
  );
}
